import { ERROR_CATEGORIES, SEVERITIES } from '../constants/reviewCatalog';
import { getTmDisplayName, resolveTmId } from './languages';
import { userDisplayName } from './adminMetrics';

export const VISUAL_FIELDS = {
  USER: 'submitted_by',
  TM: 'tm_id',
  SEVERITY: 'severity',
  ERROR_CATEGORY: 'error_category',
  SUBMISSION_TYPE: 'submission_type',
};

export function rowTmId(row, availableTMs = []) {
  return resolveTmId(row, availableTMs) || 'unknown';
}

export function countSourceWords(row) {
  if (typeof row?.source_word_count === 'number' && Number.isFinite(row.source_word_count)) {
    return Math.max(0, row.source_word_count);
  }
  const source = String(row?.source || '').trim();
  if (!source) return 0;
  return source.split(/\s+/).filter(Boolean).length;
}

export function normalizeSubmissionType(value) {
  const type = (value || 'single').toLowerCase();
  return type === 'bulk' ? 'bulk' : 'single';
}

export function applyVisualSelection(rows, selection) {
  if (!selection?.field || selection.value == null || selection.value === '') {
    return rows;
  }
  const { field, value } = selection;
  return rows.filter((row) => {
    if (field === VISUAL_FIELDS.USER) {
      return (row.submitted_by || 'unknown') === value;
    }
    if (field === VISUAL_FIELDS.SEVERITY) {
      return (row.severity || '').trim() === value;
    }
    if (field === VISUAL_FIELDS.ERROR_CATEGORY) {
      return (row.error_category || '').trim() === value;
    }
    if (field === VISUAL_FIELDS.SUBMISSION_TYPE) {
      return normalizeSubmissionType(row.submission_type) === value;
    }
    if (field === VISUAL_FIELDS.TM) {
      return rowTmId(row) === value;
    }
    return true;
  });
}

export function toggleVisualSelection(current, next) {
  if (
    current
    && next
    && current.field === next.field
    && current.value === next.value
  ) {
    return null;
  }
  return next || null;
}

export function aggregateRequestsByUser(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const user = row.submitted_by || 'unknown';
    counts.set(user, (counts.get(user) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: userDisplayName(value),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function aggregateRequestsByTm(rows, availableTMs = []) {
  const counts = new Map();
  rows.forEach((row) => {
    const tmId = rowTmId(row, availableTMs);
    counts.set(tmId, (counts.get(tmId) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: getTmDisplayName(value, availableTMs) || value,
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function aggregateWordsByUser(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const user = row.submitted_by || 'unknown';
    counts.set(user, (counts.get(user) || 0) + countSourceWords(row));
  });
  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: userDisplayName(value),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function dayKey(iso) {
  if (!iso) return '';
  const text = String(iso);
  if (text.length >= 10) return text.slice(0, 10);
  return '';
}

export function formatTrendLabel(isoDay) {
  if (!isoDay) return '';
  const date = new Date(`${isoDay}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDay;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function aggregateRequestTrend(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const key = dayKey(row.received_date || row.completed_date);
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([value, count]) => ({
      value,
      label: formatTrendLabel(value),
      count,
    }));
}

function percentOf(count, total) {
  if (total <= 0) return 0;
  return Math.round((1000 * count) / total) / 10;
}

function aggregateCatalogBreakdown(rows, field, catalog) {
  const counts = new Map();
  let classified = 0;
  rows.forEach((row) => {
    const label = (row[field] || '').trim();
    if (!label) return;
    classified += 1;
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  const known = catalog.map((entry) => entry.label);
  const extras = Array.from(counts.keys()).filter((label) => !known.includes(label)).sort();
  const ordered = [...known, ...extras].filter((label) => (counts.get(label) || 0) > 0);

  return ordered.map((label) => ({
    value: label,
    label,
    count: counts.get(label) || 0,
    percent: percentOf(counts.get(label) || 0, classified),
  }));
}

export function displaySeverityLabel(label) {
  if (label === 'Update(Non-Error)') return 'Update (Non-Error)';
  return label;
}

export function aggregateSeverity(rows) {
  return aggregateCatalogBreakdown(rows, 'severity', SEVERITIES).map((item) => ({
    ...item,
    label: displaySeverityLabel(item.label),
  }));
}

export function aggregateErrorCategory(rows) {
  return aggregateCatalogBreakdown(rows, 'error_category', ERROR_CATEGORIES);
}

export function aggregateSubmissionType(rows) {
  let single = 0;
  let bulk = 0;
  rows.forEach((row) => {
    if (normalizeSubmissionType(row.submission_type) === 'bulk') {
      bulk += 1;
    } else {
      single += 1;
    }
  });
  const total = single + bulk;
  return {
    total,
    items: [
      {
        value: 'single',
        label: 'Single',
        count: single,
        percent: percentOf(single, total),
      },
      {
        value: 'bulk',
        label: 'Bulk',
        count: bulk,
        percent: percentOf(bulk, total),
      },
    ],
  };
}

export function selectionAriaLabel(selection) {
  if (!selection) return 'No chart selection';
  return `Selected ${selection.field} ${selection.value}`;
}
